import {
  Box,
  Badge,
  Center,
  Divider,
  Group,
  Progress,
  RingProgress,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
} from '@mantine/core'
import { IconCircleCheck } from '@tabler/icons-react'
import { useMemo } from 'react'
import type { HealthScore, Incident, TopologyNode } from '@/api/topology'

export interface HealthScoreGaugeProps {
  score: HealthScore | undefined
  nodes: TopologyNode[]
  incidents: Incident[]
  loading?: boolean
}

const scoreColor = (total: number): string => {
  if (total >= 80) return 'teal'
  if (total >= 50) return 'yellow'
  return 'red'
}

const scoreLabel = (total: number): string => {
  if (total >= 80) return 'Ambiente saudável'
  if (total >= 50) return 'Atenção necessária'
  return 'Estado crítico'
}

const breakdownColor = (value: number): string => {
  if (value >= 80) return 'teal'
  if (value >= 50) return 'yellow'
  return 'red'
}

const breakdownLabels: Record<string, string> = {
  alarms: 'Alarmes',
  cpu: 'CPU',
  uptime: 'Uptime',
  errors_5xx: '5XX',
}

export function HealthScoreGauge({ score, nodes, incidents, loading }: HealthScoreGaugeProps) {
  const hostSummary = useMemo(() => ({
    healthy: nodes.filter(n => n.data.status === 'healthy').length,
    warning: nodes.filter(n => n.data.status === 'warning').length,
    critical: nodes.filter(n => n.data.status === 'critical').length,
    total: nodes.length,
  }), [nodes])

  if (loading) {
    return (
      <Stack gap="md">
        <Group>
          <Skeleton circle height={56} />
          <Stack gap={4} flex={1}>
            <Skeleton height={14} width="60%" />
            <Skeleton height={10} width="40%" />
          </Stack>
        </Group>
        <Divider />
        <SimpleGrid cols={2}>
          <Stack gap={8}>
            {[1, 2, 3, 4].map(i => <Skeleton key={i} height={8} />)}
          </Stack>
          <Stack gap={8}>
            {[1, 2, 3].map(i => <Skeleton key={i} height={32} />)}
          </Stack>
        </SimpleGrid>
      </Stack>
    )
  }

  if (!score) {
    return (
      <Center py="xl">
        <Text c="dimmed" size="sm">Dados indisponíveis</Text>
      </Center>
    )
  }

  const total = score.total
  const color = scoreColor(total)
  const trend = score.trend ?? null

  return (
    <Stack gap="md">
      {/* Header: gauge + status + hosts */}
      <Group justify="space-between" align="flex-start">
        <Group gap="md">
          <RingProgress
            size={56}
            thickness={7}
            roundCaps
            sections={[{ value: total, color }]}
            label={
              <Center>
                <Text size="xs" fw={700} c={color}>{total}</Text>
              </Center>
            }
          />
          <Stack gap={2}>
            <Text size="sm" fw={600}>{scoreLabel(total)}</Text>
            {trend !== null && (
              <Text size="xs" c={trend >= 0 ? 'teal' : 'red'}>
                {trend >= 0 ? '▲' : '▼'} {trend >= 0 ? '+' : ''}{trend} pts hoje
              </Text>
            )}
          </Stack>
        </Group>

        {hostSummary.total > 0 && (
          <Stack gap={4} align="flex-end">
            <Group gap={6}>
              <Badge size="xs" color="teal" variant="light">✓ {hostSummary.healthy}</Badge>
              <Badge size="xs" color="yellow" variant="light">⚠ {hostSummary.warning}</Badge>
              <Badge size="xs" color="red" variant="light">✗ {hostSummary.critical}</Badge>
            </Group>
            <Text size="xs" c="dimmed">{hostSummary.total} hosts</Text>
          </Stack>
        )}
      </Group>

      <Divider />

      {/* Body: breakdown | problems */}
      <SimpleGrid cols={2} spacing="md">
        {/* Left: breakdown bars */}
        <Stack gap={8}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={{ letterSpacing: '0.06em' }}>
            Breakdown
          </Text>
          {Object.entries(score.breakdown ?? {}).map(([key, value]) => (
            <Group key={key} gap={8} wrap="nowrap">
              <Text size="xs" c="dimmed" w={50} style={{ flexShrink: 0 }}>
                {breakdownLabels[key] ?? key}
              </Text>
              <Progress
                value={value}
                color={breakdownColor(value)}
                size={5}
                style={{ flex: 1 }}
              />
              <Text size="xs" c={breakdownColor(value)} w={24} ta="right">
                {value}
              </Text>
            </Group>
          ))}
        </Stack>

        {/* Right: active problems */}
        <Stack gap={8}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={{ letterSpacing: '0.06em' }}>
            Problemas ativos
          </Text>
          {incidents.length === 0 ? (
            <Group gap={6}>
              <IconCircleCheck size={14} color="var(--mantine-color-teal-6)" />
              <Text size="xs" c="teal">Nenhum problema ativo</Text>
            </Group>
          ) : (
            incidents.slice(0, 3).map((inc) => {
              const isCritical = inc.severity === 'critical'
              return (
                <Box
                  key={`${inc.date}-${inc.alarm_name}`}
                  px={8}
                  py={5}
                  style={{
                    borderLeft: `2px solid var(--mantine-color-${isCritical ? 'red' : 'yellow'}-6)`,
                    background: `var(--mantine-color-${isCritical ? 'red' : 'yellow'}-light)`,
                    borderRadius: 4,
                  }}
                >
                  <Text size="xs" fw={600} c={isCritical ? 'red' : 'yellow'} tt="uppercase">
                    {inc.severity}
                  </Text>
                  <Text size="xs" c="dimmed" lineClamp={1}>{inc.alarm_name}</Text>
                </Box>
              )
            })
          )}
        </Stack>
      </SimpleGrid>
    </Stack>
  )
}
